import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  SubscriptionsForOrganisationView,
  SubscriptionsForOrganisationService as ApiSubscriptionsForOrganisationService,
  AddSubscriptionWithChat,
  EditSubscription,
} from '@src/api';
import { TranslateService } from '@ngx-translate/core';
import { mapSubscriptionToGroupUI } from '@src/utils';
import { GroupsByOrganisationStoreService } from '@src/app/modules/group-info';

import { AlertService } from './alert.service';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionsForOrganisationService {
  private readonly subscriptionsForOrganisationService = inject(ApiSubscriptionsForOrganisationService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly groupsByOrganisationStore = inject(GroupsByOrganisationStoreService);

  associationSubscriptions$ = new BehaviorSubject<SubscriptionsForOrganisationView[] | null>(null);

  getSubscriptions(organisationId: string) {
    return this.subscriptionsForOrganisationService.organisationSubsctiptions(organisationId).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
      map(subscriptions => this.sortingSubscriptions(subscriptions)),
      map(subscriptions => subscriptions.map(subscription => mapSubscriptionToGroupUI(subscription))),
      tap(subscriptions => {
        this.groupsByOrganisationStore.setData(organisationId, subscriptions);
      }),
    );
  }

  getAssociationSubscriptions(associationId: string): void {
    this.subscriptionsForOrganisationService
      .organisationSubsctiptions(associationId)
      .pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
        map(subscriptions => this.sortingSubscriptions(subscriptions)),
      )
      .subscribe(subscriptions => this.associationSubscriptions$.next(subscriptions));
  }

  getSubscriptionsData(organisationId: string): Observable<SubscriptionsForOrganisationView[]> {
    return this.subscriptionsForOrganisationService.organisationSubsctiptions(organisationId).pipe(
      catchError(err => {
        // TODO show error notification
        return throwError(() => err);
      }),
      map(subscriptions => this.sortingSubscriptions(subscriptions)),
    );
  }

  addSubscriptions(subscriptionsData: AddSubscriptionWithChat[]) {
    return this.subscriptionsForOrganisationService.addSubscriptionList(subscriptionsData).pipe(
      catchError(err => {
        this.alertService.error(
          this.translateService.instant(
            'components.subscriptionsForOrganisationService.alerts.errors.addSubscriptionList',
          ),
        );

        return throwError(() => err);
      }),
    );
  }

  editSubscription(subscriptionData: EditSubscription) {
    return this.subscriptionsForOrganisationService.editSubscription(subscriptionData).pipe(
      catchError(err => {
        this.alertService.error(
          this.translateService.instant(
            'components.subscriptionsForOrganisationService.alerts.errors.editSubscription',
          ),
        );

        return throwError(() => err);
      }),
    );
  }

  deleteSubscription(channelId: string) {
    return this.subscriptionsForOrganisationService.deleteOrganisationSubsctiption(channelId);
  }

  private sortingSubscriptions(subscriptions: SubscriptionsForOrganisationView[]): SubscriptionsForOrganisationView[] {
    subscriptions.sort((subscription1, subscription2) => {
      const subscriptionName1 = subscription1.name?.toLowerCase();
      const subscriptionName2 = subscription2.name?.toLowerCase();

      if (!subscriptionName1) return 1;
      if (!subscriptionName2) return -1;

      if (subscriptionName1 < subscriptionName2) return -1;
      if (subscriptionName1 > subscriptionName2) return 1;
      return 0;
    });

    return subscriptions;
  }
}
